import React, { useEffect, useState, useCallback } from 'react';
import {
  FolderIcon,
  DocumentIcon,
  PhotoIcon,
  TableCellsIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { LoadingSpinner, Button } from '../../common';
import { useKGPipelineApi } from '../../../contexts/ApiContext';
import { S3AssetType, S3Object } from '../../../types/kgPipeline';

interface S3FileBrowserProps {
  bucket?: string;
  initialPrefix?: string;
  onFileSelect?: (key: string, bucket: string) => void;
}

const BUCKETS = ['kg-parsed-data', 'kg-images', 'kg-sheets', 'kg-exports', 'kg-pipeline-temp'];

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

const getFileIcon = (key: string): React.ReactNode => {
  const ext = key.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return <PhotoIcon className="h-5 w-5 text-green-500" />;
    case 'csv':
    case 'parquet':
    case 'xlsx':
      return <TableCellsIcon className="h-5 w-5 text-blue-500" />;
    case 'md':
    case 'txt':
      return <DocumentIcon className="h-5 w-5 text-gray-500" />;
    case 'json':
      return <DocumentIcon className="h-5 w-5 text-yellow-500" />;
    case 'pkl':
      return <DocumentIcon className="h-5 w-5 text-purple-500" />;
    default:
      return <DocumentIcon className="h-5 w-5 text-gray-400" />;
  }
};

const getFileType = (key: string): S3AssetType | 'UNKNOWN' => {
  const ext = key.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return 'IMAGE';
    case 'csv':
      return 'SHEET_CSV';
    case 'parquet':
      return 'SHEET_PARQUET';
    case 'md':
      return 'MARKDOWN';
    case 'json':
      return 'STRUCTURE_JSON';
    case 'pkl':
      return 'PICKLE';
    default:
      return 'UNKNOWN';
  }
};

export const S3FileBrowser: React.FC<S3FileBrowserProps> = ({ bucket: initialBucket, initialPrefix = '', onFileSelect }) => {
  const kgPipelineApi = useKGPipelineApi();
  const [selectedBucket, setSelectedBucket] = useState(initialBucket || BUCKETS[0]);
  const [currentPrefix, setCurrentPrefix] = useState(initialPrefix);
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [s3Healthy, setS3Healthy] = useState<boolean | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  const checkS3Health = useCallback(async () => {
    try {
      const health = await kgPipelineApi.getS3Health();
      setS3Healthy(health.enabled && health.healthy);
      if (!health.enabled) {
        setError('S3/MinIO storage is not enabled');
      } else if (!health.healthy) {
        setError(health.error || 'S3/MinIO is not healthy');
      }
    } catch {
      setS3Healthy(false);
      setError('Failed to check S3 health');
    }
  }, [kgPipelineApi]);

  // Fetch objects from S3 bucket
  const fetchObjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await kgPipelineApi.listBucket(selectedBucket, currentPrefix);

      // Map backend response to S3Object interface
      const mappedObjects: S3Object[] = response.objects.map((obj) => ({
        key: obj.key,
        size: obj.size,
        lastModified: obj.last_modified,
        isFolder: obj.is_folder
      }));

      setObjects(mappedObjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch objects');
    } finally {
      setLoading(false);
    }
  }, [kgPipelineApi, selectedBucket, currentPrefix]);

  useEffect(() => {
    checkS3Health();
  }, [checkS3Health]);

  useEffect(() => {
    if (s3Healthy) {
      fetchObjects();
    }
  }, [s3Healthy, fetchObjects]);

  const handleBucketChange = (bucket: string) => {
    setSelectedBucket(bucket);
    setCurrentPrefix('');
    setPreviewUrl(null);
    setPreviewKey(null);
  };

  const handleNavigate = (path: string) => {
    setCurrentPrefix(path);
    setPreviewUrl(null);
    setPreviewKey(null);
  };

  const handlePreview = async (key: string) => {
    try {
      const response = await kgPipelineApi.getPresignedUrl(selectedBucket, key);
      setPreviewUrl(response.url);
      setPreviewKey(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get preview URL');
    }
  };

  const handleDownload = async (key: string) => {
    try {
      const response = await kgPipelineApi.getPresignedUrl(selectedBucket, key, 300);
      window.open(response.url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get download URL');
    }
  };

  // Build breadcrumb path
  const breadcrumbParts = currentPrefix.split('/').filter(Boolean);

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 dark:border-border-dark">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text">S3 Browser</h3>
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Browse KG Pipeline S3 buckets</p>
          </div>
          <div className="flex items-center space-x-2">
            {s3Healthy !== null && (
              <span className={`rounded-full px-3 py-1 text-sm ${s3Healthy ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {s3Healthy ? 'Connected' : 'Disconnected'}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={fetchObjects} disabled={loading}>
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Bucket Selector */}
      <div className="border-b border-gray-200 p-4 dark:border-border-dark">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary">Bucket</label>
        <div className="flex flex-wrap gap-2">
          {BUCKETS.map((bucket) => (
            <button
              key={bucket}
              onClick={() => handleBucketChange(bucket)}
              className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                selectedBucket === bucket
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              }`}
            >
              {bucket}
            </button>
          ))}
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="border-b border-gray-200 px-4 py-2 dark:border-border-dark">
        <div className="flex items-center space-x-1 text-sm">
          <button
            onClick={() => handleNavigate('')}
            className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <HomeIcon className="mr-1 h-4 w-4" />
            {selectedBucket}
          </button>
          {breadcrumbParts.map((part, index) => (
            <React.Fragment key={index}>
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              <button
                onClick={() => handleNavigate(breadcrumbParts.slice(0, index + 1).join('/') + '/')}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {part}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex">
        {/* File List */}
        <div className={`flex-1 ${previewUrl ? 'border-r border-gray-200 dark:border-border-dark' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : objects.length === 0 ? (
            <div className="py-12 text-center">
              <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-dark-text">No files</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-secondary">
                {s3Healthy ? 'This location is empty or S3 listing is not implemented yet.' : 'S3/MinIO is not connected.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {objects.map((obj) => (
                <div
                  key={obj.key}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-dark-background-tertiary/50"
                >
                  <div className="flex items-center space-x-3">
                    {obj.isFolder ? <FolderIcon className="h-5 w-5 text-yellow-500" /> : getFileIcon(obj.key)}
                    <button
                      onClick={() => (obj.isFolder ? handleNavigate(obj.key) : onFileSelect?.(obj.key, selectedBucket))}
                      className="font-medium text-gray-900 hover:text-blue-600 dark:text-dark-text dark:hover:text-blue-400"
                    >
                      {obj.key.split('/').pop() || obj.key}
                    </button>
                  </div>
                  <div className="flex items-center space-x-4">
                    {!obj.isFolder && (
                      <>
                        <span className="text-sm text-gray-500 dark:text-dark-text-secondary">{formatFileSize(obj.size)}</span>
                        <span className="text-sm text-gray-500 dark:text-dark-text-secondary">{formatDate(obj.lastModified)}</span>
                        <div className="flex items-center space-x-1">
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(obj.key)} title="Preview">
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(obj.key)} title="Download">
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview Panel */}
        {previewUrl && previewKey && (
          <div className="w-1/2 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-medium text-gray-900 dark:text-dark-text">Preview</h4>
              <button onClick={() => setPreviewUrl(null)} className="text-gray-500 hover:text-gray-700 dark:text-dark-text-secondary">
                ×
              </button>
            </div>
            <p className="mb-2 truncate text-sm text-gray-500 dark:text-dark-text-secondary">{previewKey}</p>
            {getFileType(previewKey) === 'IMAGE' ? (
              <img src={previewUrl} alt={previewKey} className="max-h-96 rounded-lg object-contain" />
            ) : (
              <div className="rounded-lg bg-gray-100 p-4 dark:bg-dark-background-tertiary">
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                  Preview not available for this file type.
                  <br />
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Download to view
                  </a>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default S3FileBrowser;
